import React, { useEffect, useState } from "react";
import { Typography, Container, Grid2, Card, Avatar } from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../../../firestore-DB-config/firebase-config";
import { styled } from "@mui/system";
import Header from "../../../Header/Header";
import Footer from "../../../Footer/Footer";

// Interface for Team Member
interface TeamMember {
  id: string;
  name: string;
  role: string;
  description: string;
  imageUrl: string;
}

const TeamCard = styled(Card)(({ theme }) => ({
  maxWidth: "100%",
  minHeight: "340px",
  borderRadius: "15px",
  background: "linear-gradient(219.84deg, #474747 4.14%, #222222 44.22%)", // Gradient background
  color: "#fff",
  textAlign: "center",
  boxShadow: "0px 15px 40px rgba(0, 0, 0, 0.15)", // Softer, more natural shadow
  padding: theme.spacing(4), // Increased padding for a spacious layout
  display: "flex",
  flexDirection: "column", // Ensure the content stacks vertically
  alignItems: "center", // Center the content horizontally
  justifyContent: "space-between", // Space content equally
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
  "&:hover": {
    transform: "translateY(-10px)", // Subtle lift on hover
    boxShadow: "0px 20px 50px rgba(0, 0, 0, 0.3)", // Stronger shadow on hover
  },
  [theme.breakpoints.up("md")]: {
    maxWidth: "300px", // Control max width on larger screens for better alignment
  },
}));

const AvatarImage = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(16),
  height: theme.spacing(16),
  marginBottom: theme.spacing(2),
  boxShadow: "0px 5px 15px rgba(0, 0, 0, 0.1)", // Add subtle shadow to avatar for a polished look
  border: "3px solid #ffffff", // Add a white border to make the avatar stand out
}));

// TeamPage Component
const TeamPage: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);

  const fetchTeamMembers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "teamMembers"));
      const teamList = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as TeamMember[];
      setTeamMembers(teamList);
    } catch (error) {
      console.error("Error fetching team members: ", error);
    }
  };

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  return (
    <>
      <Header />
      <Container sx={{ my: { xs: 6, md: 9 }, px: { xs: 3, md: 5 } }}>
        <Typography
          sx={{ fontSize: { xs: 28, md: 40 }, lineHeight: "1.3em" }}
          fontWeight={700}
          mb={6}
          color="#011D33"
          textAlign="center"
        >
          Meet Our Talented Team
        </Typography>
        <Typography
          variant="body1"
          sx={{
            textAlign: "center",
            fontSize: { xs: 16, md: 18 },
            color: "#5F5F5F",
            mb: 6,
            maxWidth: "800px",
            mx: "auto",
          }}
        >
          We are a group of dedicated professionals committed to delivering the
          best results. Get to know the people behind our success.
        </Typography>
        <Grid2 container spacing={4} justifyContent="center">
          {teamMembers.map((member) => (
            <Grid2 size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={member.id}>
              <TeamCard>
                <AvatarImage alt={member.name} src={member.imageUrl} />
                <Typography
                  sx={{
                    fontWeight: "600",
                    fontSize: { xs: 16, md: 20 },
                    mb: 1,
                  }}
                >
                  {member.name}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ fontWeight: "500", color: "#AAAAAA", mb: 2 }}
                >
                  {member.role}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: 14, md: 16 },
                    color: "#CCCCCC",
                    textAlign: "center",
                  }}
                >
                  {member.description}
                </Typography>
              </TeamCard>
            </Grid2>
          ))}
        </Grid2>
      </Container>
      <Footer />
    </>
  );
};

export default TeamPage;
